import { addTicket } from 'api/canpark/tickets/addTicket';
import { getActiveTourKey } from 'components/onboarding/tour/activeTour';
import { Ticket } from '../types/ticket';
import { ParkingLocation } from '../types/parkingLocation';

type Stage = 'action' | 'carPhoto' | 'ticket' | 'confirm' | 'towInfo';

const CANPARK_TICKETING_TOUR_KEY = 'canpark_issue_ticket';

interface SubmitTicketContext {
	userId: string;
	parkingLocation: ParkingLocation | null;
	notifySuccess: (message: string) => void;
	notifyError: (message: string) => void;
	resetForm: () => void;
	setStage: (stage: Stage) => void;
	closeDrawer: () => void;
}

const hasTowInstructions = (location: ParkingLocation | null): boolean =>
	!!(
		location?.towInstructions ||
		location?.towingCompanyName ||
		location?.towingCompanyNumber
	);

export const submitTicket = async (
	data: Ticket,
	ctx: SubmitTicketContext,
): Promise<boolean> => {
	try {
		// The guided tour is a sandbox: follow the same post-save UI flow without
		// creating a record. This shared submit path covers both direct saves and
		// lots that show the Review stage before Save.
		if (getActiveTourKey() === CANPARK_TICKETING_TOUR_KEY) {
			ctx.notifySuccess(
				'Sandbox walkthrough complete—no real record was created.',
			);
			ctx.resetForm();
			if (data.ticketType === 'tow' && hasTowInstructions(ctx.parkingLocation)) {
				ctx.setStage('towInfo');
			} else {
				ctx.closeDrawer();
			}
			return true;
		}

		await addTicket({ ...data, issuerUserId: ctx.userId });
		ctx.notifySuccess('Record saved successfully');
		ctx.resetForm();
		if (data.ticketType === 'tow' && hasTowInstructions(ctx.parkingLocation)) {
			ctx.setStage('towInfo');
		} else {
			ctx.closeDrawer();
		}
		return true;
	} catch (error) {
		console.error('[submitTicket] Failed to create ticket:', error);
		ctx.notifyError('Failed to save record. Please try again.');
		return false;
	}
};
